import type { ReactNode } from 'react';
import { Component, type ErrorInfo } from 'react';
import { useEffect, useState } from 'react';
import { documentReadRefusalText, t } from '@bms2/shared';
import { Icon } from '../components/Icon';
import { PageModule } from './PageModule';
import styles from './shell.module.css';
import { reportBrowserError } from './systemErrors';

/**
 * The fallback states (piece 12), each rendered inside the shell rather than as
 * a silent redirect or a blank page. 🔨 Access Denied explains what is
 * restricted while withholding both the breadcrumb and the record name, so it
 * never leaks the content it refuses to show.
 */
export function AccessDenied({ reason }: { reason?: string | undefined } = {}) {
  // 🔒 A document read refused for want of the account or the Deal's team says so in the server's words (decision 1632); any other refusal keeps the one sentence, which names nothing.
  const documentRead = reason === documentReadRefusalText();
  return (
    <PageModule title={t('shell.accessDenied.title')}>
      <div className={styles.fallback} data-fallback="accessDenied" data-denied-reason={documentRead ? 'documentRead' : 'generic'}>
        <Icon meaning="accessDenied" size="header" />
        <p className={styles.fallbackTitle}>{t('shell.accessDenied.title')}</p>
        <p>{documentRead ? documentReadRefusalText() : t('shell.accessDenied.body')}</p>
      </div>
    </PageModule>
  );
}

/** A route that resolves to no record and no nav item, inside the shell. */
export function NotFound() {
  return (
    <PageModule title={t('shell.notFound.title')}>
      <div className={styles.fallback} data-fallback="notFound">
        <p className={styles.fallbackTitle}>{t('shell.notFound.title')}</p>
        <p>{t('shell.notFound.body')}</p>
      </div>
    </PageModule>
  );
}

/**
 * The generic boundary, for an unexpected render or API failure outside a
 * component's own documented failure state — Address Verification's one-hour
 * retry hold being the only failure state the specification itself defines.
 *
 * 🔒 It reports the fault as a `SystemError` row and shows the row's reference
 * under the fallback, so the owner can find it on Settings › System › System
 * errors (piece 9; decision 0822; T271). The console line stays as it was.
 */
export class ShellErrorBoundary extends Component<{ children: ReactNode; apiBaseUrl?: string }, { failed: boolean; reference: string | null }> {
  constructor(props: { children: ReactNode; apiBaseUrl?: string }) {
    super(props);
    this.state = { failed: false, reference: null };
  }

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('shell: an unexpected render failure', error, info.componentStack);
    void reportBrowserError(error, this.props.apiBaseUrl ?? '', info.componentStack ?? null).then((reference) => {
      if (reference) this.setState({ reference });
    });
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      <div className={styles.fallback} data-fallback="error">
        <Icon meaning="warning" size="header" />
        <p className={styles.fallbackTitle}>{t('shell.error.title')}</p>
        <p>{t('shell.error.body')}</p>
        {this.state.reference ? <p data-error-reference={this.state.reference}>{[t('shell.error.reference'), this.state.reference].join(' ')}</p> : null}
      </div>
    );
  }
}

/**
 * A non-blocking connectivity banner. The iPhone-to-desktop requirement means
 * network reliability varies far more than on a desktop-only application, so the
 * shell says so rather than letting a screen fail silently.
 */
export function OfflineBanner() {
  const [offline, setOffline] = useState(() => typeof navigator !== 'undefined' && navigator.onLine === false);
  useEffect(() => {
    const online = () => setOffline(false);
    const lost = () => setOffline(true);
    window.addEventListener('online', online);
    window.addEventListener('offline', lost);
    return () => {
      window.removeEventListener('online', online);
      window.removeEventListener('offline', lost);
    };
  }, []);
  if (!offline) return null;
  return (
    <div className={styles.offlineBanner} role="status" data-shell="offline-banner">
      <Icon meaning="offline" size="inline" /> {t('shell.offline')}
    </div>
  );
}
